<script lang="ts">
	import toISOString from "../libs/date/format";
	import type { DialerAgentConfiguration } from "../libs/app/configuration";
	import type { Campaign } from "../app/campaign";
	import type { Writable } from "svelte/store";
	import { api, dialerAgent } from "../bootstrap";
	import { navigate } from "svelte-navigator";
	import { telno2intl } from "../libs/telno2intl/telno2intl";
	import { toast } from "@zerodevx/svelte-toast";
	import { onDestroy, onMount } from "svelte";

	let name = "";
	let telno = "";
	let externalId = "";
	let schedule = false;
	let scheduledDate = toISOString(new Date(), false);
	let scheduledTime = "";
	let campaign: number;
	let scheduledExten: number;
	let campaigns: Array<Campaign> | false = [];
	let campaignsPromise: Promise<Array<Campaign> | false>;

	$: disabledSave = checkDisabledSave(
		name,
		telno,
		schedule,
		scheduledDate,
		scheduledTime,
		campaign
	);

	const configuration: Writable<DialerAgentConfiguration> =
		dialerAgent.configuration;

	if ($configuration.sip.length === 1) {
		scheduledExten = parseInt($configuration.sip[0].exten);
	}

	function checkDisabledSave(
		name: string,
		telno: string,
		schedule: boolean,
		scheduledDate: string,
		scheduledTime: string,
		campaign: number
	): boolean {
		if (!name || !telno || !campaign) {
			return true;
		}

		if (schedule === true && (!scheduledTime || !scheduledDate)) {
			return true;
		}

		const r = telno2intl(telno);
		if (r.ton === 1) {
			return false;
		}

		return true;
	}

	async function send() {
		let timeScheduled = null;
		if (schedule === true) {
			timeScheduled = scheduledDate + " " + scheduledTime;
		}

		let data = {
			name,
			telno,
			external_id: externalId,
			campaign,
			scheduled_exten: scheduledExten,
			time_scheduled: timeScheduled,
		};

		const result = await api.addTarget(data);

		if (result !== false) {
			toast.push("Nový klient kampane bol pridaný");
			navigate("/main/summary");
			return;
		}
	}

	function exit() {
		navigate("/main/summary");
	}

	api.pause();
	campaignsPromise = api.campaigns();
	campaignsPromise.then((_campaigns: Array<Campaign> | false) => {
		campaigns = _campaigns;

		if (campaigns !== false && campaigns.length > 0) {
			campaign = parseInt(campaigns[0].id);
		}
	});

	onDestroy(() => {
		api.unpause();
	});
</script>

<svelte:head>
	<title>Nový klient kampane | DialerAgent</title>
</svelte:head>

<div class="container mt-3">
	{#await campaignsPromise}
	<!-- Čakanie na dotiahnutie aktívnych kampaní -->
	{:then _campaigns}
		{#if campaigns.length === 0}
			<div class="alert alert-warning" role="alert">
				<div class="d-flex justify-content-between">
					<span
						>Nie je aktívna žiadna kampaň! Pridanie nového klienta nie
						je možné!</span>
					<button
						class="btn btn-primary"
						on:click|preventDefault={exit}>Koniec</button>
				</div>
			</div>
		{:else}
			<div class="inner-container">
				<h2>Nový klient kampane</h2>

				<form class="row g-3">
					<div class="col-md-12">
						<label for="campaign" class="form-label">Kampaň</label>
						<select
							class="form-select"
							name="campaign"
							id="campaign"
							bind:value={campaign}>
							{#each campaigns as campaign}
								<option value={campaign.id}
									>{campaign.name}</option>
							{/each}
						</select>
					</div>

					<div class="col-md-12">
						<label for="exten" class="form-label"
							>Prepojiť na klapku</label>
						<select
							class="form-select"
							name="exten"
							id="exten"
							bind:value={scheduledExten}>
							<option value="-1">Nezáleží</option>
							{#each $configuration.sip as sip}
								<option value={sip.exten}
									>{sip.displayname}</option>
							{/each}
						</select>
					</div>

					<div class="col-md-12">
						<label for="name" class="form-label">Meno</label>
						<input
							type="text"
							class="form-control"
							id="name"
							bind:value={name} />
					</div>

					<div class="col-md-12">
						<label for="telno" class="form-label"
							>Telefónne číslo</label>
						<input
							type="text"
							class="form-control"
							id="telno"
							bind:value={telno} />
						<div id="emailHelp" class="form-text">
							Číslo zadajte v národnom tvare, alebo v
							medzinárodnom tvare s dvoma nulami, alebo znakom
							plus na začiatku. Nevkladajte medzi číslice žiadne
							znaky.
						</div>
					</div>

					<div class="col-md-12">
						<label for="external_id" class="form-label">Identifikátor</label>
						<input
							type="text"
							class="form-control"
							id="external_id"
							bind:value={externalId} />
					</div>

					<div class="col-md-12">
						<input
							type="checkbox"
							class="form-check-input"
							id="schedule"
							bind:checked={schedule} />
						<label class="form-check-label" for="schedule"
							>Volať v určený čas</label>
					</div>

					{#if schedule === true}
						<div class="col-md-6">
							<label for="date" class="form-label">Dátum</label>
							<input
								type="date"
								class="form-control"
								id="date"
								bind:value={scheduledDate} />
						</div>

						<div class="col-md-6">
							<label for="time" class="form-label">Čas</label>
							<input
								type="time"
								class="form-control"
								id="time"
								bind:value={scheduledTime} />
						</div>
					{/if}

					<div class="btn-group">
						<button
							type="submit"
							class="btn btn-primary"
							on:click|preventDefault={send}
							disabled={disabledSave}>Uložiť</button>
						<button
							type="submit"
							class="btn btn-primary"
							on:click|preventDefault={exit}>Koniec</button>
					</div>
				</form>
			</div>
		{/if}
	{/await}
</div>

<style>
	.inner-container {
		max-width: 600px;
		margin: 0 auto;
	}
</style>
